import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { auth, firestore, getInitializationPromise } from '../firebase';
import { 
  onAuthStateChanged, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  sendPasswordResetEmail, 
  signOut, 
  updateProfile,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence
} from 'firebase/auth';
import { doc, setDoc, serverTimestamp, getDoc, updateDoc, collection, query, limit, getDocs } from 'firebase/firestore';
import { getWorkspaces, addUserToWorkspace } from '../services/firebaseService';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [error, setError] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userStatus, setUserStatus] = useState('online'); // 'online', 'offline', 'doNotDisturb'
  const [firebaseInitialized, setFirebaseInitialized] = useState(false);
  const [authPersistence, setAuthPersistence] = useState(true);
  const [updatingProfile, setUpdatingProfile] = useState(false);
  
  // Add a ref to track if auth state has been initialized
  const authStateInitialized = useRef(false);
  
  // Track initial auth check completion
  const initialAuthCheckComplete = useRef(false);

  // Check Firebase initialization
  useEffect(() => {
    let isMounted = true;
    
    const checkFirebaseInit = async () => {
      try {
        await getInitializationPromise();
        console.log('Firebase initialization confirmed');
        
        if (isMounted) {
        setFirebaseInitialized(true);
        
          // Check if we should use persistent auth based on localStorage
          const shouldPersist = localStorage.getItem('bloomRememberMe') === 'true';
          const persistenceType = shouldPersist ? browserLocalPersistence : browserSessionPersistence;
          
          // Set persistence based on remembered preference
        try {
            await setPersistence(auth, persistenceType);
            console.log(`Auth persistence set to ${shouldPersist ? 'LOCAL' : 'SESSION'} based on remembered preference`);
            
            if (isMounted) {
              setAuthPersistence(shouldPersist);
            }
        } catch (persistenceError) {
          console.error('Failed to set auth persistence:', persistenceError);
          }
        }
      } catch (error) {
        console.error('Firebase initialization failed:', error);
        if (isMounted) {
        setError('Failed to initialize Firebase. Please refresh the page.');
        }
      }
    };
    
    checkFirebaseInit();
    
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      console.log('App is online');
      setIsOnline(true);
      setError(null);
    };

    const handleOffline = () => {
      console.log('App is offline');
      setIsOnline(false);
      setError('You are currently offline. Some features may be limited.');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load user profile data
  useEffect(() => {
    let isMounted = true;
    
    const loadUserProfile = async (user) => {
      if (!user || !isOnline) return;
      
      try {
        console.log('Loading user profile for:', user.uid);
        const userDocRef = doc(firestore, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists() && isMounted) {
          console.log('User profile found');
          setUserProfile(userDoc.data());
        } else if (isMounted) {
          console.log('No user profile found, creating one');
          const newProfile = {
            email: user.email,
            displayName: user.displayName || user.email?.split('@')[0] || 'User',
            createdAt: serverTimestamp(),
            lastLogin: serverTimestamp()
          };
          
          // Create the user profile if it doesn't exist
          await setDoc(userDocRef, newProfile);
          setUserProfile(newProfile);
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
      }
    };
    
    if (currentUser) {
      loadUserProfile(currentUser);
    } else if (isMounted) {
      setUserProfile(null);
    }
    
    return () => {
      isMounted = false;
    };
  }, [currentUser, isOnline]);

  // Register new user
  async function register(email, password, displayName) {
    try {
      if (!isOnline) {
        throw new Error('Cannot register while offline. Please check your internet connection.');
      }

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Create a user document
      await setDoc(doc(firestore, 'users', userCredential.user.uid), {
        email,
        displayName: displayName || email.split('@')[0],
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp()
      });
      
      return userCredential.user;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  // Login with email and password
  const login = useCallback(async (email, password) => {
    if (!firebaseInitialized) {
      throw new Error('Firebase is not initialized');
    }
    
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Update last login time
      try {
        const userRef = doc(firestore, 'users', result.user.uid);
        await updateDoc(userRef, {
          lastLogin: serverTimestamp()
        });
      } catch (updateError) {
        console.warn('Failed to update last login time:', updateError);
      }
      
      // When a user logs in, ensure they have access to at least one workspace
      await ensureWorkspaceAccess(result.user);
      
      return result;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }, [firebaseInitialized]);
  
  // Log out user
  async function logout() {
    try {
      // Clear persistence flags
      localStorage.removeItem('bloomAuthPersisted');
      
      await signOut(auth);
      return true;
    } catch (error) {
      console.error('Logout error:', error);
      return false;
    }
  }

  // Send password reset email
  async function resetPassword(email) {
    try {
      if (!isOnline) {
        throw new Error('Cannot reset password while offline. Please check your internet connection.');
      }
      
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  }

  // Update user profile data
  async function updateUserProfile(updates) {
    try {
      if (!currentUser || !isOnline) {
        throw new Error('Cannot update profile while offline or not logged in.');
      }
      
      // Set flag to prevent logout during profile updates
      setUpdatingProfile(true);
      
      const userDocRef = doc(firestore, 'users', currentUser.uid);
      
      // Add timestamp to updates
      const updatedData = {
        ...updates,
        updatedAt: serverTimestamp()
      };
      
      // First, update the Firestore document
      await setDoc(userDocRef, updatedData, { merge: true });
      
      // Then update Firebase Auth profile if displayName was changed
      if (updates.displayName) {
        try {
          console.log('Updating Firebase Auth display name...');
          await updateProfile(currentUser, {
            displayName: updates.displayName
          });
          console.log('Auth profile updated successfully');
        } catch (authError) {
          console.error('Error updating Auth profile:', authError);
          // Don't throw here - we already updated Firestore successfully
        }
      }
      
      // Update local state regardless of auth update success
      setUserProfile(prev => ({
        ...prev,
        ...updates
      }));
      
      return true;
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    } finally {
      // Reset flag after update completes (success or failure)
      setUpdatingProfile(false);
    }
  }
  
  // Update user status
  async function updateUserStatus(status) {
    try {
      // Immediately update local state for UI feedback regardless of user/online state
      setUserStatus(status);
      setUserProfile(prevProfile => {
        const newProfile = {
          ...prevProfile,
          status
        };
        console.log('Immediately updated user profile state with new status:', newProfile);
        return newProfile;
      });
      
      // Broadcast status change to all components
      document.dispatchEvent(new CustomEvent('bloomStatusChanged', { 
        detail: { status }
      }));
      
      // If user is not logged in or offline, we can't update Firestore
      if (!currentUser || !isOnline) {
        console.log('Cannot update Firestore status: user not logged in or offline');
        return true; // Still return true since we updated local state
      }

      setUpdatingProfile(true);
      
      // Update Firestore
      try {
        const userDocRef = doc(firestore, 'users', currentUser.uid);
        await updateDoc(userDocRef, { 
          status,
          lastStatusChange: serverTimestamp()
        });
        console.log(`Firestore status updated to: ${status}`);
      } catch (firestoreError) {
        console.error('Firestore status update failed:', firestoreError);
        // Continue since we already updated local state
      }
      
      setUpdatingProfile(false);
      return true;
    } catch (error) {
      console.error('Error updating user status:', error);
      setUpdatingProfile(false);
      return false;
    }
  }

  // Helper function to get status text
  const getStatusText = (status) => {
    switch (status) {
      case 'online': return 'Online';
      case 'away': return 'Away';
      case 'offline': return 'Offline';
      case 'doNotDisturb': return 'Do Not Disturb';
      case 'sleeping': return 'Sleeping';
      case 'inMeeting': return 'In a Meeting';
      case 'onVacation': return 'On Vacation';
      case 'sick': return 'Sick';
      default: return 'Online';
    }
  };

  // Listen for auth state changes
  useEffect(() => {
    console.log('Setting up auth state change listener. Firebase initialized:', firebaseInitialized);
    if (!firebaseInitialized) return;
    
    // Forcibly set persistence to LOCAL to ensure user remains logged in
    setPersistence(auth, browserLocalPersistence)
      .then(() => {
        console.log('AUTH: Persistence set to LOCAL');
      })
      .catch((error) => {
        console.error('AUTH: Error setting persistence:', error);
      });
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('AUTH: Auth state changed:', user ? `User ${user.uid}` : 'No user');
      
      if (user) {
        // User is signed in
        setCurrentUser(user);
        
        // Mark local storage for auth persistence check
        localStorage.setItem('bloomAuthPersisted', 'true');
        
        // Update last active timestamp
        try {
          const userRef = doc(firestore, 'users', user.uid);
          await updateDoc(userRef, {
            lastActive: serverTimestamp()
          });
        } catch (error) {
          console.warn('Failed to update last active timestamp:', error);
        }
      } else {
        // User is signed out
        setCurrentUser(null);
        
        // Check if we should auto-login (e.g., after a page refresh)
        const shouldAutoLogin = localStorage.getItem('bloomAuthPersisted') === 'true';
        if (shouldAutoLogin) {
          console.log('AUTH: Detected potential logout during page refresh. User should be persisted.');
        }
      }
      
      // Mark auth check as complete
      initialAuthCheckComplete.current = true;
      setLoading(false);
      
      // Mark auth state as initialized
      authStateInitialized.current = true;
    });
    
    // Clean up subscription
    return unsubscribe;
  }, [firebaseInitialized]);

  // When a user logs in, ensure they have access to at least one workspace
  const ensureWorkspaceAccess = async (user) => {
    if (!user) return;
    
    try {
      // Check if the user has any workspaces
      const userWorkspaces = await getWorkspaces(user.uid);
      
      if (userWorkspaces.length === 0) {
        // No workspaces found, try to find a default workspace to add them to
        const workspacesRef = collection(firestore, 'workspaces');
        const workspacesQuery = query(workspacesRef, limit(1));
        const workspacesSnapshot = await getDocs(workspacesQuery);
        
        if (!workspacesSnapshot.empty) {
          // Found at least one workspace, add the user to it
          const defaultWorkspace = workspacesSnapshot.docs[0];
          await addUserToWorkspace(
            defaultWorkspace.id,
            user.uid,
            user.email || 'unknown@email.com'
          );
          console.log('Added user to existing workspace:', defaultWorkspace.id);
        } else {
          // No workspaces exist yet, will be handled when user accesses dashboard
          console.log('No workspaces found to add user to');
        }
      }
    } catch (error) {
      console.error('Error ensuring workspace access:', error);
    }
  };

  const value = {
    currentUser,
    userProfile,
    register,
    login,
    logout,
    resetPassword,
    loading,
    isOnline,
    error,
    authPersistence,
    updateUserProfile,
    updateUserStatus,
    userStatus,
    setAuthPersistence,
    updatingProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
